import { localStorageService } from './localStorage';
import type { Climb } from '../types/climb';

/**
 * The last answer to a climbs-list request, kept so the next opening of that
 * list paints at once and refreshes behind the rows rather than in front of a
 * skeleton — the same stale-while-revalidate the feed and the user profile do.
 *
 * A gym's list is the biggest thing the app stores by far (a few hundred KB
 * for a wall of two hundred problems), and localStorage is a small, shared
 * drawer: the auth token lives in it too. So only a handful of lists are kept
 * — the ones opened most recently — and the rest are let go before a write.
 *
 * The key names the reader as well as the list (see ClimbsList): the rows say
 * whether *you* have sent each climb, and one person's copy must not be the
 * next person's first paint.
 */
const PREFIX = 'climbs_list_';
const KEEP = 3;
/** Beyond this the copy is more likely to mislead than to help. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const climbsListCache = {
  read(key: string): Climb[] | null {
    const cached = localStorageService.get<Climb[]>(PREFIX + key);
    return Array.isArray(cached) && cached.length > 0 ? cached : null;
  },

  write(key: string, climbs: Climb[]): void {
    // Oldest first, so what goes is what was opened longest ago. The key
    // being written is refreshed, not counted against the rest.
    const others = localStorageService.entries(PREFIX).filter(entry => entry.key !== PREFIX + key);
    others.slice(0, Math.max(0, others.length - (KEEP - 1))).forEach(entry => localStorageService.remove(entry.key));

    localStorageService.set(PREFIX + key, climbs, TTL_MS);
  },
};
